/* eslint complexity: ["error", { max: 7 }] -- applies to entire file */
import { useCallback, useMemo } from "react";

import isNil from "lodash/isNil";

import { ProgramCode, ProgramName } from "@/constants/TransitionTrack";
import { isUserPreTreatment } from "@/hooks/usePatientStatus";
import { useGetFullProfileQuery } from "@/store/api/user";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

const isTransitionTrackProgram = (programName?: null | string) =>
  !isNil(programName) && programName === ProgramName;

const isTransitionTrackTreatmentEpisode = (episode: TreatmentEpisodeProgram) =>
  episode.programName === ProgramName ||
  episode.treatmentProgramCode === ProgramCode;
type ProfileWithStatus = {
  universalStatusCode: UniversalStatusCode;
} & LinkedPatientProfile;

const hasUniversalStatusCode = (
  profile: LinkedPatientProfile,
): profile is ProfileWithStatus => !isNil(profile.universalStatusCode);

interface TransitionTrackStatus {
  isUserInTransitionTrack: (userId?: string) => boolean;
  wasUserEverInTransitionTrack: (userId?: string) => boolean;
}

const useTransitionTrackStatus = (): TransitionTrackStatus => {
  const { data: appUserProfile } = useGetFullProfileQuery();

  const userProfilesMap: Record<string, LinkedPatientProfile> = useMemo(() => {
    if (isNil(appUserProfile)) return {};
    const linkedProfiles = appUserProfile.linkedPatients ?? [];
    const userProfiles = [appUserProfile, ...linkedProfiles];
    const initialMap = {};
    return userProfiles.reduce(
      // eslint-disable-next-line max-params
      (resultMap, profile) => ({
        ...resultMap,
        [profile.externalId]: profile,
      }),
      initialMap,
    );
  }, [appUserProfile]);

  const getUserProfile = useCallback(
    (userId?: string): ProfileWithStatus | undefined => {
      if (!isNotNilNotEmpty(userId)) return undefined;
      const user = userProfilesMap[userId];
      if (isNil(user) || !hasUniversalStatusCode(user)) return undefined;
      return user;
    },
    [userProfilesMap],
  );

  const isUserInTransitionTrack = useCallback(
    (userId?: string) => {
      const user = getUserProfile(userId);
      if (isNil(user)) return false;

      const { programName, targetTreatmentProgram, universalStatusCode } = user;

      return isUserPreTreatment(universalStatusCode)
        ? isTransitionTrackProgram(targetTreatmentProgram)
        : isTransitionTrackProgram(programName);
    },
    [getUserProfile],
  );

  const wasUserEverInTransitionTrack = useCallback(
    (userId?: string) => {
      // Check if user is currently in Transition Track
      if (isUserInTransitionTrack(userId)) return true;

      // Check if in-treatment user was previously in Transition Track
      const user = getUserProfile(userId);
      if (isNil(user)) return false;

      if (isNil(user.treatmentEpisodePrograms)) return false;

      const { treatmentEpisodePrograms, universalStatusCode } = user;

      return (
        !isUserPreTreatment(universalStatusCode) &&
        treatmentEpisodePrograms.some(isTransitionTrackTreatmentEpisode)
      );
    },
    [getUserProfile, isUserInTransitionTrack],
  );

  return {
    isUserInTransitionTrack,
    wasUserEverInTransitionTrack,
  };
};

export default useTransitionTrackStatus;
